import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  MeasurementSchema,
  MeasurementCreateInputSchema,
  MeasurementUpdateInputSchema,
  type Measurement,
  type MeasurementCreateInput,
  type MeasurementUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `measurement` (valeur chiffree horodatee, org-wide en v0).
 *
 * Replique le PATRON `asset` : l'entree est VALIDEE par Zod
 * (`MeasurementCreateInputSchema`) AVANT toute ecriture Firestore. Les champs
 * standard sont remplis par le SDK, les requetes `list` sont cadrees par orgId.
 *
 * Securite : org-wide en v0 (meme doctrine qu'asset et careRecord). La
 * visibilite reelle herite du `targetRef` (mesure sur un sujet de soin = niveau
 * sante ; equipement ou lieu = org) ; ce cloisonnement fin viendra apres v0.
 */

export type { MeasurementCreateInput };

/** Nom de la collection Firestore de la famille measurement. */
export const MEASUREMENTS_COLLECTION = 'measurements';

/**
 * Repository type pour la famille `measurement`.
 * Toute lecture est validee par `MeasurementSchema`, toute ecriture par
 * `MeasurementCreateInputSchema` (cage IA : aucun acces Firestore direct hors SDK).
 */
export interface MeasurementRepository {
  create(input: MeasurementCreateInput): Promise<Measurement>;
  update(id: string, patch: MeasurementUpdateInput): Promise<Measurement>;
  list(options?: ListOptions): Promise<Measurement[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Measurement | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Measurement>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Measurement>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Measurement>;
}

const measurementConverter = createConverter<Measurement>((raw: DocumentData) => MeasurementSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifie courant
 *   (injecte par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createMeasurementRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): MeasurementRepository {
  const col = collection(db, MEASUREMENTS_COLLECTION).withConverter(measurementConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Measurement>(db, col, MEASUREMENTS_COLLECTION, getActorId);

  return {
    async create(input: MeasurementCreateInput): Promise<Measurement> {
      // Valider AVANT d'ecrire : une entree invalide echoue ici, rien n'atteint
      // Firestore. (Meme doctrine que le patron asset.)
      const fields = MeasurementCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs metier optionnels : on ne les ecrit que s'ils sont fournis
      // (evite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.assetId !== undefined) optional['assetId'] = fields.assetId;
      if (fields.placeId !== undefined) optional['placeId'] = fields.placeId;
      if (fields.careRecordId !== undefined) optional['careRecordId'] = fields.careRecordId;
      if (fields.unit !== undefined) optional['unit'] = fields.unit;
      if (fields.measuredAt !== undefined) optional['measuredAt'] = fields.measuredAt;
      if (fields.measuredByWorkerId !== undefined) optional['measuredByWorkerId'] = fields.measuredByWorkerId;
      if (fields.inspectionId !== undefined) optional['inspectionId'] = fields.inspectionId;
      if (fields.invoiceId !== undefined) optional['invoiceId'] = fields.invoiceId;
      if (fields.workOrderId !== undefined) optional['workOrderId'] = fields.workOrderId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        metric: fields.metric,
        value: fields.value,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Measurement);

      // setDoc ne resout pas serverTimestamp() : on valide une projection
      // coherente avec ce qui vient d'etre ecrit.
      return MeasurementSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: MeasurementUpdateInput): Promise<Measurement> {
      // Valider AVANT d'écrire : même doctrine que create.
      const fields = MeasurementUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Seules les clés réellement fournies sont écrites (patch partiel, merge).
      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'écriture partielle : une ref convertie
      // exigerait le document complet.
      await updateDoc(doc(db, MEASUREMENTS_COLLECTION, id), data);

      // Relire via la ref convertie : le document réel est la source de vérité,
      // et le converter le valide avec MeasurementSchema.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`measurement ${id} introuvable après mise à jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Measurement[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Measurement | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
